import { Grid, TextField } from '@mui/material';
import _ from 'lodash';
import React, { ChangeEvent, forwardRef, useEffect, useState } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import './StartEndDatepicker.scss';
import "react-datepicker/dist/react-datepicker.css";
import Utility from '../../app/utility';
import moment from 'moment'

interface StartEndDatepickerProp {
    startDateValue?: string,
    endDateValue?: string,
    startDateLabel?: string,
    endDateLabel?: string,
    useQuickEndDateInput: boolean,
    className?: string,
    disabled?: boolean,
    useSingleMode?: boolean,
    allowFreeInput?: boolean,
    hideDateSelector?: boolean,
    startDateError?: boolean,
    endDateError?: boolean,
    disableAutoYear?: boolean,
    customFormatDate?: (date: Date) => string,
    onStartDateChange?: (date: string) => void,
    onEndDateChange?: (date: string) => void
}

const StartEndDatepicker: React.FunctionComponent<StartEndDatepickerProp> = (props) => {
    let [startDateInputValue, setStartDateInputValue] = useState<string>("");
    let [startDateParseError, setStartDateParseError] = useState<boolean>(false);

    let [endDateInputValue, setEndDateInputValue] = useState<string>("");
    let [endDateParseError, setEndDateParseError] = useState<boolean>(false);

    useEffect(() => {
        let startDateValue = props.startDateValue ? props.startDateValue : "";
        setStartDateInputValue(startDateValue);
    }, [props.startDateValue]);

    useEffect(() => {
        let endDateValue = props.endDateValue ? props.endDateValue : "";
        setEndDateInputValue(endDateValue);
    }, [props.endDateValue]);

    const getInputValue = (input: string, errorFunc: any, startDate: string = "") => {
        /*
            input is number only -> day of the month
            if day of the month >= today  -> current month, else -> next month
        */
        if (isNumber(input)) {
            let startDateDate = new Date(startDate);
            let dayOfMonth = Number(input);
            let today = new Date();
            let selectedDate = new Date(
                today.getFullYear(),
                dayOfMonth >= today.getDate() ? today.getMonth() : today.getMonth() + 1,
                dayOfMonth);

            if (startDate.length > 0 && selectedDate.getTime() < startDateDate.getTime()) {
                selectedDate = new Date(
                    startDateDate.getFullYear(),
                    dayOfMonth >= startDateDate.getDate() ? startDateDate.getMonth() : startDateDate.getMonth() + 1,
                    dayOfMonth
                );
            }

            if (dayOfMonth === selectedDate.getDate()) {
                if (props.customFormatDate) {
                    return props.customFormatDate(selectedDate);
                }

                return Utility.DateFormat.format(selectedDate).toUpperCase();
            } else {
                errorFunc(true);
                Utility.Alert.Error("This day of month does not exist");
            }
        } else {
            let formatedDate = moment(input);
            if (formatedDate.format() === 'Invalid date') {
                errorFunc(true);
                Utility.Alert.Error("Invalid Date");
            } else {
                if (!props.allowFreeInput) {
                    if (formatedDate.toDate().getFullYear() === 2001) {
                        formatedDate.set("year", (new Date()).getFullYear());

                        if (formatedDate.toDate().getTime() < new Date().getTime()) {
                            formatedDate.set("year", (new Date()).getFullYear() + 1);
                        }
                    }

                    if (startDate.length > 0 && formatedDate.toDate().getTime() < new Date(startDate).getTime()) {
                        errorFunc(true);
                        Utility.Alert.Error("End date must be after start date");
                    }
                } else {
                    if (formatedDate.toDate().getFullYear() === 2001) {
                        if (props.disableAutoYear) {
                            Utility.Alert.Error("Missing Year");
                            return input.toUpperCase();
                        }

                        //case default year
                        formatedDate.set("year", (new Date()).getFullYear());

                        if (formatedDate.toDate().getTime() < new Date().getTime()) {
                            formatedDate.set("year", (new Date()).getFullYear() + 1);
                        }
                    }
                }

                if (props.customFormatDate) {
                    return props.customFormatDate(formatedDate.toDate());
                }
                return Utility.DateFormat.format(formatedDate.toDate()).toUpperCase();
            }
        }

        return input;
    }

    const isNumber = (value: string | number): boolean => {
        return ((value != null) &&
            (value !== '') &&
            !isNaN(Number(value.toString())));
    }

    useEffect(() => {
    }, []);


    const CustomDatepicker = React.forwardRef<
        HTMLButtonElement,
        React.DetailedHTMLProps<
            React.ButtonHTMLAttributes<HTMLButtonElement>,
            HTMLButtonElement
        >
    >((datepickerProps, ref) => {
        return <>
            <button
                onClick={datepickerProps.onClick}
                ref={ref}
                className='startenddatepicker-calander'
                tabIndex={-1}
            >
                <i className="fa fa-calendar" aria-hidden="true"></i>
            </button>
        </>;
    });

    return <div
        className={props.className ? props.className : ""}
    >
        <Grid
            container
            className={props.hideDateSelector ? "" : "start-end-datepicker"}
        >
            <Grid item xs={props.useSingleMode ? 12 : 6}>
                <TextField
                    label={props.startDateLabel ? props.startDateLabel : "Start Date"}
                    value={startDateInputValue}
                    disabled={props.disabled === true ? true : false}
                    onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                        setStartDateInputValue(e.currentTarget.value.toUpperCase());
                        setStartDateParseError(false);
                    }}
                    error={startDateParseError || (props.startDateError == true ? true : false)}
                    onBlur={() => {
                        if (startDateInputValue && startDateInputValue.length > 0) {
                            let dateValue = getInputValue(startDateInputValue, setStartDateParseError).toUpperCase();
                            setStartDateInputValue(dateValue);

                            if (!startDateParseError) {
                                if (props.onStartDateChange) {
                                    props.onStartDateChange(dateValue);
                                }

                                if (props.useQuickEndDateInput) {
                                    let endDate = Utility.addDaysToDate(moment(dateValue).toDate(), 5);

                                    if (props.customFormatDate) {
                                        setEndDateInputValue(props.customFormatDate(endDate));
                                    } else {
                                        setEndDateInputValue(Utility.DateFormat.format(endDate).toUpperCase());
                                    }


                                    if (props.onEndDateChange) {
                                        if (props.customFormatDate) {
                                            props.onEndDateChange(props.customFormatDate(endDate));
                                        } else {
                                            props.onEndDateChange(Utility.DateFormat.format(endDate).toUpperCase());
                                        }
                                    }
                                }
                            }
                        } else {
                            if (props.onStartDateChange) {
                                props.onStartDateChange("");
                            }
                        }
                    }}
                />
                {
                    !props.hideDateSelector &&
                    <ReactDatePicker
                        disabled={props.disabled}
                        className={(props.disabled ? "email-disable" : "")}
                        onChange={(date) => {
                            if (date) {
                                let dateValue = Utility.DateFormat.format(date).toUpperCase();
                                if (props.customFormatDate) {
                                    dateValue = props.customFormatDate(moment(dateValue).toDate());
                                }

                                setStartDateInputValue(dateValue);

                                if (props.onStartDateChange) {
                                    props.onStartDateChange(dateValue);
                                }

                                if (props.useQuickEndDateInput) {
                                    let endDate = Utility.addDaysToDate(moment(dateValue).toDate(), 5);

                                    if (props.customFormatDate) {
                                        setEndDateInputValue(props.customFormatDate(endDate));
                                    } else {
                                        setEndDateInputValue(Utility.DateFormat.format(endDate).toUpperCase());
                                    }

                                    if (props.onEndDateChange) {

                                        if (props.customFormatDate) {
                                            props.onEndDateChange(props.customFormatDate(endDate));
                                        } else {
                                            props.onEndDateChange(Utility.DateFormat.format(endDate).toUpperCase());
                                        }
                                    }
                                }
                            }
                        }}
                        customInput={<CustomDatepicker />}
                    >
                    </ReactDatePicker>
                }
            </Grid>
            {
                !props.useSingleMode &&
                <Grid item xs={6}>
                    <TextField
                        label={props.endDateLabel ? props.endDateLabel : "End Date"}
                        value={endDateInputValue}
                        disabled={props.disabled === true ? true : false}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                            setEndDateInputValue(e.currentTarget.value);
                            setEndDateParseError(false);
                        }}
                        error={endDateParseError || (props.endDateError == true ? true : false)}
                        onBlur={() => {
                            if (endDateInputValue && endDateInputValue.length > 0) {
                                let dateValue = getInputValue(endDateInputValue, setEndDateParseError, startDateInputValue);
                                setEndDateInputValue(dateValue);

                                if (!endDateParseError) {
                                    if (props.onEndDateChange) {
                                        props.onEndDateChange(dateValue);
                                    }
                                }
                            } else {
                                if (props.onEndDateChange) {
                                    props.onEndDateChange("");
                                }
                            }
                        }}
                    />
                    {
                        !props.hideDateSelector &&
                        <ReactDatePicker
                            className={(props.disabled ? "email-disable" : "")}
                            disabled={props.disabled}
                            onChange={(date) => {
                                if (date) {
                                    let dateValue = Utility.DateFormat.format(date).toUpperCase();
                                    if (props.customFormatDate) {
                                        dateValue = props.customFormatDate(date);
                                    }
                                    setEndDateInputValue(dateValue);

                                    if (props.onEndDateChange) {
                                        props.onEndDateChange(dateValue);
                                    }
                                }
                            }}
                            customInput={<CustomDatepicker />}
                        >
                        </ReactDatePicker>
                    }
                </Grid>
            }
        </Grid>
    </div>;
};

export default StartEndDatepicker;